import { authClient, signIn } from "@d12labs/auth/client";
import { apiFetch } from "@d12labs/client";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { loginFieldErrors } from "@/auth/login-validate";
import { BrandLockup } from "@/components/brand/brand-lockup";

type Providers = { microsoft?: boolean };

export function LoginPage() {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [emailError, setEmailError] = useState<string | null>(null);
	const [passwordError, setPasswordError] = useState<string | null>(null);
	const [formError, setFormError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const navigate = useNavigate();
	const { data: providers } = useQuery({
		queryKey: ["account", "providers"],
		queryFn: () => apiFetch<Providers>("/api/account/providers"),
		staleTime: Infinity,
	});
	const microsoft = providers?.microsoft === true;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const errors = loginFieldErrors(email, password);
		setEmailError(errors.email ?? null);
		setPasswordError(errors.password ?? null);
		setFormError(null);
		if (errors.email || errors.password) return;
		setSubmitting(true);
		const { error: signInError } = await signIn.email({ email: email.trim(), password });
		setSubmitting(false);
		if (signInError) {
			setFormError(signInError.message ?? "Email or password is incorrect.");
			return;
		}
		await navigate({ to: "/home" });
	};

	const signInWithMicrosoft = async () => {
		const { error } = await authClient.signIn.social({
			provider: "microsoft",
			callbackURL: "/home",
		});
		if (error) setFormError(error.message ?? "Microsoft sign-in failed");
	};

	return (
		<div className="auth-ember">
			<main className="ground">
				<div className="seam" />
				<div className="grid" />
				<div className="glow" />
				<div className="shell">
					<BrandLockup />
					<h1>Sign in</h1>
					<p className="lede">Access is invite-only. Use the account you were invited with.</p>
					<div className="card">
						{microsoft ? (
							<>
								<button className="btn btn-sso" type="button" onClick={signInWithMicrosoft}>
									<MicrosoftMark />
									Sign in with Microsoft
								</button>
								<div className="or">
									<span />
									<b>or continue with email</b>
									<span />
								</div>
							</>
						) : null}
						<form className="fields" onSubmit={handleSubmit} noValidate>
							<label>
								<span className="lab">Email</span>
								<span className={emailError ? "inp is-invalid" : "inp"}>
									<MailIcon />
									<input
										id="email"
										type="email"
										name="email"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										autoComplete="email"
										placeholder="you@company.com"
										aria-invalid={emailError ? true : undefined}
										aria-describedby={emailError ? "email-error" : undefined}
									/>
								</span>
								{emailError ? (
									<span id="email-error" className="field-err" role="alert">
										{emailError}
									</span>
								) : null}
							</label>
							<label>
								<span className="lab">
									Password <Link to="/forgot-password">Forgot?</Link>
								</span>
								<span className={passwordError ? "inp is-invalid" : "inp"}>
									<input
										id="password"
										type={showPassword ? "text" : "password"}
										name="password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										autoComplete="current-password"
										placeholder="••••••••••"
										aria-invalid={passwordError ? true : undefined}
										aria-describedby={passwordError ? "password-error" : undefined}
									/>
									<button
										className="reveal"
										type="button"
										aria-label={showPassword ? "Hide password" : "Show password"}
										onClick={() => setShowPassword((v) => !v)}
									>
										{showPassword ? <EyeOffIcon /> : <EyeIcon />}
									</button>
								</span>
								{passwordError ? (
									<span id="password-error" className="field-err" role="alert">
										{passwordError}
									</span>
								) : null}
							</label>
							{formError ? (
								<p className="form-err" role="alert">
									{formError}
								</p>
							) : null}
							<button className="btn btn-primary" type="submit" disabled={submitting}>
								{submitting ? "Signing in…" : "Sign in"}
								<ArrowIcon />
							</button>
						</form>
					</div>
					<p className="foot">
						<ShieldIcon />
						Invite-only · 2FA supported
						<span className="sep">·</span>
						<a href="mailto:info@d12labs.com?subject=Request%20access">Need access?</a>
					</p>
				</div>
			</main>
		</div>
	);
}

function MicrosoftMark() {
	return (
		<svg viewBox="0 0 16 16" aria-hidden="true">
			<rect x="1.5" y="1.5" width="6" height="6" fill="#F25022" />
			<rect x="8.5" y="1.5" width="6" height="6" fill="#7FBA00" />
			<rect x="1.5" y="8.5" width="6" height="6" fill="#00A4EF" />
			<rect x="8.5" y="8.5" width="6" height="6" fill="#FFB900" />
		</svg>
	);
}

function MailIcon() {
	return (
		<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
			<rect x="2" y="3.5" width="12" height="9" rx="1.2" />
			<path d="m2.5 4.5 5.5 4 5.5-4" />
		</svg>
	);
}

function EyeIcon() {
	return (
		<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
			<path d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8Z" />
			<circle cx="8" cy="8" r="2" />
		</svg>
	);
}

function EyeOffIcon() {
	return (
		<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
			<path d="M2 2.5 14 13.5" />
			<path d="M6.1 6.4A2 2 0 0 0 8 10c.5 0 1-.2 1.4-.5" />
			<path d="M3.2 5.2C2 6.3 1.5 8 1.5 8S4 12.5 8 12.5c.8 0 1.5-.2 2.2-.5" />
			<path d="M6.5 3.8A8 8 0 0 1 8 3.5C12 3.5 14.5 8 14.5 8c-.3.6-.8 1.3-1.4 2" />
		</svg>
	);
}

function ArrowIcon() {
	return (
		<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
			<path d="M3 8h10m-4-4 4 4-4 4" />
		</svg>
	);
}

function ShieldIcon() {
	return (
		<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
			<path d="M8 2 3 4v4c0 3 2.2 5 5 6 2.8-1 5-3 5-6V4L8 2Z" />
		</svg>
	);
}
